"use client";

import * as api from "@/src/api/sdk.gen";
import { useEffect, useRef } from "react";
import {  useNavigate  } from "react-router-dom";
import { AccessError } from "@/components/student/AccessError";
import LessonPlayerWithCourseCache from "@/components/student/LessonPlayerWithCourseCache";
import { useQuery } from "@tanstack/react-query";

type LessonPayload = {
    id: string;
    title: string;
    structuredContent?: unknown;
    moduleId: number;
    moduleTitle: string;
    canonicalLessonId: number;
    language: "English" | "Arabic";
    /** True while the workplace-scenario block is still personalizing in the background. */
    blockPersonalizationPending?: boolean;
    availableLanguages?: string[];
    languageVariants?: Record<string, string>;
};

type LessonApiResult =
    | { kind: "ok" | "generating"; lesson: LessonPayload }
    | { kind: "notFound" }
    | { kind: "forbidden" };

export default function LessonRouteClient({
    courseId,
    lessonId,
}: {
    courseId: string;
    lessonId: string;
}) {
    const navigate = useNavigate();
    const activeKeyRef = useRef(`${courseId}:${lessonId}`);
    useEffect(() => {
        activeKeyRef.current = `${courseId}:${lessonId}`;
    }, [courseId, lessonId]);

    const lessonQuery = useQuery({
        queryKey: ["lesson-payload", courseId, lessonId],
        queryFn: async () => {
            const { data: rawData, error, response } = await api.getLessonPayloadApiV1CoursesCourseIdLessonsLessonIdPayloadGet({
                path: { course_id: courseId, lesson_id: lessonId },
            });
            if (response?.status === 404) throw new Error("notFound");
            if (response?.status === 403) throw new Error("forbidden");
            if (error) throw new Error("error");
            const data = rawData as LessonApiResult;
            if (data.kind === "forbidden") throw new Error("forbidden");
            if (data.kind !== "ok" && data.kind !== "generating") throw new Error("notFound");
            return data.lesson;
        },
        staleTime: 30_000,
        refetchOnMount: true,
        // While the workplace-scenario block is still being personalized in
        // the background, poll until it's ready so the page picks up the
        // real content on its own instead of requiring a manual refresh.
        refetchInterval: (query) => (query.state.data?.blockPersonalizationPending ? 4000 : false),
    });
    const lesson = lessonQuery.data ?? null;
    const errorKind = lessonQuery.error ? (lessonQuery.error as Error).message : null;

    useEffect(() => {
        if (errorKind !== "forbidden") return;
        navigate(`/courses/${courseId}?prep=1`);
    }, [errorKind, courseId]);

    if (errorKind === "forbidden") {
        return (
            <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 px-4 text-center text-sm text-muted-foreground">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" aria-hidden />
                <p>Preparing your lesson…</p>
            </div>
        );
    }
    if (errorKind === "notFound") {
        return (
            <AccessError
                title="Lesson not found"
                message="This lesson link appears invalid or no longer available."
                primaryHref={`/courses/${courseId}`}
                primaryLabel="Back to course"
            />
        );
    }
    if (errorKind === "error") {
        return (
            <AccessError
                title="Something went wrong"
                message="We couldn't load this lesson. Please try again."
                primaryHref={`/courses/${courseId}`}
                primaryLabel="Back to course"
            />
        );
    }

    if (lessonQuery.isLoading || !lesson) {
        return (
            <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 px-4 text-center text-sm text-muted-foreground">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" aria-hidden />
                <p>Loading lesson…</p>
            </div>
        );
    }

    return (
        <LessonPlayerWithCourseCache
            urlCourseId={courseId}
            lesson={lesson}
            lessonIsLoading={false}
        />
    );
}
